import React, { useMemo, useState } from "react";
import { useProfileParams } from "../../../../../../utils/common";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import DonutChart, {
  DonutChartSegment,
} from "../../../../../common/DonutChart/DonutChart";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import { useTranslation } from "react-i18next";
import ViewModeSwitcher, {
  ViewMode,
} from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import GenericTable from "../../../../../common/GenericTable/GenericTable";
import StackedGroupedBarChart from "../../../../../common/GroupedStackedBarChart/GroupedStackedBarChart";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import sharedStyles from "../../../../ABOGrowthCompanion/common/SharedAGCStyles.module.scss";
import KPITableWithPills from "../../../common/KPITableWithPills/KPITableWithPills";
import { sanitizeTableData } from "../../../../../../utils/expandableTableDataSanitizer";
import { stackedBarDataSanitizer } from "../../../../../../utils/stackedBarChartDataSanitizer";
import KPIAccordionTable from "../../../common/KPIAccordionTable/KPIAccordionTable";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const BusinessLine: React.FC<SummaryProp> = ({
  summary,
  loading = false,
}) => {
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments)
    ? summary!.segments
    : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t("business_line")}
      dataTestId="business-line-tile"
      loading={loading}
    >
      <div className={sharedStyles.tile_content}>
        <DonutChart
          size={125}
          strokeWidth={20}
          legendGap={24}
          segments={segments}
          showLegend
          isPortfolio
        />
      </div>
    </KPITile>
  );
};

const BusinessLineYearlyView: React.FC<{
  viewMode: ViewMode;
  tableData: any;
  stackedBarChartData?: any;
  kpiCode: string;
}> = ({ viewMode, tableData, stackedBarChartData, kpiCode }) => {
  const { t } = useTranslation();
  let component;

  if (viewMode === "left") {
    component = isEmpty(stackedBarChartData) ? (
      <div className={sharedStyles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <StackedGroupedBarChart
        data={stackedBarChartData}
        showPills={true}
        isShowAllPill={true}
        currency="USD"
      />
    );
  } else {
    component = isEmpty(tableData) ? (
      <div className={sharedStyles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <GenericTable
        tableData={tableData}
        kpiCode={kpiCode}
        highlightColumns={[1, 3]}
        groupedColumnClass={true}
      />
    );
  }

  return (
    <div
      className={`${sharedStyles.header_content} ${viewMode === "left" ? sharedStyles.container : ""}`}
    >
      <div className={sharedStyles.tile_subheader}>
        <div className={sharedStyles.title}>
          {t("businessline.businessLineYearlySubtitle")}
        </div>
        <div className={sharedStyles.desc}>
          {t("businessline.businessLineMonthlySubHeader")}
        </div>
      </div>
      {component}
    </div>
  );
};

const BusinessLineMonthlyView: React.FC<{
  tableData: any;
  kpiCode: string;
  affiliateCode?: string;
}> = ({ tableData, kpiCode, affiliateCode }) => {
  const { t } = useTranslation();

  const component = isEmpty(tableData) ? (
    <div className={sharedStyles.no_data}>{t("noDataAvailable")}</div>
  ) : (
    <KPIAccordionTable
      data={tableData}
      affiliateCode={affiliateCode}
      kpiCode={kpiCode}
    />
  );

  return (
    <div className={sharedStyles.header_content}>
      <div className={sharedStyles.tile_subheader}>
        <div className={sharedStyles.title}>
          {t("businessline.businessLineMonthlySubtitle")}
        </div>
        <div className={sharedStyles.desc}>
          {t("businessline.businessLineMonthlySubHeader")}
        </div>
      </div>
      {component}
    </div>
  );
};

export const BusinessLineDetails: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = "business-line", onClose }) => {
  type View = "perfYear" | "monthly" | "yearly";
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("left");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("perfYear");

  const isLoadingAiSummary = false;

  const { data: detailsResponse, isLoading } =
    useGetBusinessPerformanceDetailsQuery(
      { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
      { skip: !onClose }
    );

  const kpiData = useMemo(() => {
    if (detailsResponse?.body?.kpis) {
      return detailsResponse.body.kpis.find((k: any) => k.kpiCode === kpiCode);
    }
    return null;
  }, [detailsResponse, kpiCode]);

  const ytdViewData = kpiData?.detail?.ytdView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === "table"
  )?.chartDetail?.tableDetail;

  const yearlyRawTableData = kpiData?.detail?.anuallyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === "table"
  )?.chartDetail?.tableDetail;

  const yearlyTableData = useMemo(
    () => sanitizeTableData(yearlyRawTableData),
    [yearlyRawTableData]
  );

  const yearlyRawStackedBarData = kpiData?.detail?.anuallyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === "barChart"
  )?.chartDetail?.barChartDetail;

  const yearlyStackedBarData = useMemo(
    () => stackedBarDataSanitizer(yearlyRawStackedBarData),
    [yearlyRawStackedBarData]
  );

  const monthlyRawTableData = kpiData?.detail?.monthlyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === "table"
  )?.chartDetail?.tableDetail;

  const monthlyTableData = useMemo(
    () => sanitizeTableData(monthlyRawTableData),
    [monthlyRawTableData]
  );

  const viewMap: Record<View, JSX.Element> = {
    perfYear: (
      <KPITableWithPills
        data={ytdViewData}
        affiliateCode={affiliateCode}
        title="businessline.businessLinePYSubtitle"
        subtitle="businessline.businessLineMonthlySubHeader"
      />
    ),
    monthly: (
      <BusinessLineMonthlyView
        tableData={monthlyTableData}
        kpiCode={kpiCode}
        affiliateCode={affiliateCode}
      />
    ),
    yearly: (
      <BusinessLineYearlyView
        viewMode={viewMode}
        tableData={yearlyTableData}
        stackedBarChartData={yearlyStackedBarData}
        kpiCode={kpiCode}
      />
    ),
  };

  const perfYear = "PY 25"; // TODO Change to real performance year from API
  const periodItems = useMemo(
    () => [
      { code: "perfYear", label: perfYear },
      { code: "monthly", label: t("monthly_view") },
      { code: "yearly", label: t("yearly_view") },
    ],
    [t]
  );

  const headerContent = (
    <div className={sharedStyles.header_content}>
      <div className={sharedStyles.tile_header}>
        <div className={sharedStyles.title}>{t("businessline.title")}</div>
        <div className={sharedStyles.desc}>{t("businessline.description")}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        showSwitch={selectedPeriod === "yearly"}
        selectedTimeRangeCode={selectedPeriod}
        onSelectTimeRange={setSelectedPeriod}
        timeRanges={periodItems}
      />
    </div>
  );

  const leftContent = viewMap[selectedPeriod as View] ?? null;

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={""}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  if (isLoading) return <Skeleton height="400px" />;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="business-line-detail"
    />
  );
};

export default BusinessLine;
